import React, { Suspense, useEffect, useState } from "react";

import {
	RouteComponentProps,
	useRouteMatch,
	withRouter
} from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import axios, { AxiosError } from "axios";

import "./Dashboard.less";

import {
	AuthToken,
	cache,
	QueryParamConstants,
	ZomentumRoutes,
	ZomentumStorageKeys,
	ZomentumWindow
} from "@/Contracts";

import LoginActionCreators from "@/Actions/Login";
import UsersActionCreators from "@/Actions/User";

import {
	ChildException,
	ExceptionType,
	ServerError,
	ServerValidationErrorResponse
} from "@/Components/Utils/ServerError";
import { DashboardLayout } from "../DashboardLayout";
import ZContentLoadingIcon from "@atoms/ZContentLoadingIcon";

import { UIConfigActionCreators } from "@/Actions/UI";

import PreSetupAccountLoadThirdPartyScripts from "./PreSetupAccountLoadThirdPartyScripts";
import PostSetupAccountLoadThirdPartyScripts from "./PostSetupAccountLoadThirdPartyScripts";
import ZMessage from "@molecules/ZMessage";
import { Auth0ContextInterface, useAuth0 } from "@auth0/auth0-react";
import SalesActivityDrawers from "@/SalesActivity/pages/Drawers/SalesActivities";
import Banner from "../Banner";
import CancelledAccountModal from "@pages/Authentication/modals/CancelledAccountModal";
import OnBoardingModal from "../OnBoardingModal";
import { EditProductToQuoteBlockDrawer } from "@/Components/DocumentsPage/Drawers/DocumentProductDrawers/EditProductToQuoteBlockDrawer";
import { setupDone } from "@/V2Utils";
import { fetchHostRedirectionUrl } from "@/Contracts/Utils";
import { cacheConstant } from "@/Contracts/appCache";
import useMobileView from "@/Hooks/useMobileView";
import { Auth0LoginState } from "@/Contracts/Login";
import { GlobalState } from "@/Reducers/interface";
import { fileReader } from "@/Utils/ProposalBuilder";
import { ZModal } from "@atoms";

import ZPaymentDueBanner from "@/V2Components/UI/organisms/ZPaymentDueBanner";
import { matchPath } from "react-router-dom";
import {
	getAppPathname,
	prependBasePath
} from "@/Utils/routing/getAppPathname";
import { isEmbedded } from "@/Utils/superops/embedDetection";
import {
	isSuperOpsReauthReloadInProgress,
	markSuperOpsReauthReloadInProgress
} from "@/Utils/superops/superOpsReauthState";

const PartnerDashboardRoutes = React.lazy(
	() => import("./DashboardRoutes/PartnerDashboardRoutes")
);

declare let window: ZomentumWindow;

if (process.env.REACT_APP_SHA) {
	window.REACT_APP_SHA = process.env.REACT_APP_SHA;
}

export interface Props extends RouteComponentProps {
	updateServiceWorker: (registration: ServiceWorkerRegistration) => void;
	auth0LoginState: Auth0LoginState;
}

export const AppDashboard: React.FC<Props> = props => {
	const auth0 = useAuth0();
	const dispatch = useDispatch();
	const logout = (auth0: Auth0ContextInterface) =>
		dispatch(LoginActionCreators.logout(auth0));

	const isOnAuthorize =
		useRouteMatch(ZomentumRoutes.Authorize)?.isExact ?? false;
	const sessionInitialized = useSelector(
		(state: GlobalState) => state.login.sessionInitialized
	);

	const [tokenReady, setTokenReady] = useState(false);
	useEffect(() => {
		if (auth0.isLoading) return;
		// While /authorize is mounted, Authorize.tsx owns the bootstrap and
		// dispatches sessionInitialized on success. Dashboard must not race
		// it with a duplicate auth'd fetch against a stale cached token.
		// The ~8 redundant API calls in HAR phase 1 came from this race.
		// Still flip tokenReady true so PartnerDashboardRoutes renders and
		// the Authorize route component actually mounts — skipping only the
		// dispatch work, not the render gate.
		if (isOnAuthorize) {
			setTokenReady(true);
			return;
		}
		if (auth0.isAuthenticated) {
			(async () => {
				let token: string;
				try {
					token = await auth0.getAccessTokenSilently();
				} catch (err) {
					console.error("[Auth] getAccessTokenSilently failed:", err);
					if (isEmbedded()) {
						ZMessage.error(
							"Session expired. Please reload the SuperOps page."
						);
						setTokenReady(true);
						return;
					}
					auth0.loginWithRedirect({
						appState: {
							return_to_url:
								window.location.pathname +
								window.location.search
						}
					});
					return;
				}

				const authToken = new AuthToken(token);
				cache.save(ZomentumStorageKeys.Token, authToken);

				const cachedConfig = cache.get(
					cacheConstant.configurationSettings,
					true
				);
				const hostRedirectionUrl = fetchHostRedirectionUrl(
					authToken,
					cachedConfig
				);
				if (hostRedirectionUrl) {
					window.location.href = hostRedirectionUrl;
					return;
				}

				// Skip the duplicate getInitialConfig if /authorize already
				// populated the store via bootstrapAuthenticatedSession.
				// sessionInitialized is an explicit Redux flag set only on
				// successful bootstrap, NOT a truthiness probe on
				// configurationSettings (which defaults to a truthy empty
				// object from day one and is not a valid readiness signal).
				if (!sessionInitialized) {
					try {
						await dispatch(
							UsersActionCreators.getInitialConfig() as any
						);
					} catch (err) {
						console.error("[Config] getInitialConfig failed:", err);
					}
				}

				setTokenReady(true);
			})();
		} else {
			const hint = new URLSearchParams(window.location.search).get(
				"id_token_hint"
			);

			if (hint && isEmbedded()) {
				const stripped = new URLSearchParams(window.location.search);
				stripped.delete("id_token_hint");
				const rawReturnTo =
					window.location.pathname +
					(stripped.toString() ? "?" + stripped.toString() : "");
				const returnTo = getAppPathname(rawReturnTo);
				if (returnTo && returnTo !== "/") {
					cache.save(ZomentumStorageKeys.RETURN_TO_URL, returnTo);
				}
				setTokenReady(true);

				const region = new URLSearchParams(window.location.search).get(
					"region"
				);
				const authorizeUrl =
					`${
						ZomentumRoutes.Authorize
					}?superops_hint=${encodeURIComponent(hint)}` +
					(region ? `&region=${encodeURIComponent(region)}` : "");
			
				props.history.push(authorizeUrl);
				return;
			}
			setTokenReady(true);
		}
	}, [
		auth0.isLoading,
		auth0.isAuthenticated,
		isOnAuthorize,
		sessionInitialized
	]);

	const isMobileView = useMobileView();

	const isPdfView = useRouteMatch(ZomentumRoutes.DocumentsPdfView)?.isExact;
	const {
		currentActiveOpportunity,
		currentActiveClient,
		userData,
		currentActiveReport,
		currentActivePipeline,
		favicon
	} = useSelector((state: GlobalState) => ({
		currentActiveOpportunity: state.opportunities.currentActiveOpportunity,
		currentActiveClient: state.clients.currentActiveClient,
		userData: state.user.userData,
		currentActiveReport: state.reports.currentActiveReport,
		currentActivePipeline: state.pipelines.currentActivePipeline,
		favicon: state.user.userCompany.favicon
	}));

	const [isPasswordlessError, setIsPasswordlessError] = useState(false);

	const showPasswordLessLinkExpired = () => {
		setIsPasswordlessError(true);

		// show modal error
		ZModal.error({
			title: "Signup Link Expired",
			content:
				"The link you are trying to access has expired. We have resent the activation link on your registered email id.",
			onOk: () => {
				logout(auth0);
			},
			onCancel: () => {
				logout(auth0);
			},
			closable: false,
			maskClosable: false
		});
	};

	const checkForPasswordLessError = async () => {
		const error = window.location.hash;
		if (error.includes("unauthorized") && !auth0.isAuthenticated) {
			showPasswordLessLinkExpired();
		}
	};

	useEffect(() => {
		const currFavicon = document.getElementById(
			"favicon"
		) as HTMLLinkElement;
		if (favicon && currFavicon) {
			currFavicon.href = favicon;
		}
	}, []);

	useEffect(() => {
		if (!auth0.isLoading) checkForPasswordLessError();
	}, [auth0.isLoading]);

	useEffect(() => {
		if (
			props.auth0LoginState &&
			Object.keys(props.auth0LoginState)?.length !== 0
		) {
			dispatch(
				LoginActionCreators.auth0LoginStateReceived(
					props.auth0LoginState
				)
			);
		}
	}, [props.auth0LoginState]);

	useEffect(() => {
		dispatch(UIConfigActionCreators.updateMobileView(isMobileView));
	}, [isMobileView]);

	useEffect(() => {
		axios.interceptors.request.use(
			request => request,
			error => {
				return Promise.reject(error);
			}
		);
		axios.interceptors.response.use(
			response => {
				return response;
			},
			async (error: AxiosError<ServerValidationErrorResponse>) => {
				if (
					error.request?.responseType === "blob" &&
					error.response?.data instanceof Blob &&
					error.response.data.type?.toLowerCase().indexOf("json") !=
						-1
				) {
					const { data } = error.response;
					// Read file
					const file = await fileReader(data);
					error.response.data = await JSON.parse(file as any);
				}

				const skipServerErrorBannerStatusCodes =
					error.config?.headers[
						"Skip-Server-Error-Banner-Status-Codes"
					] ?? "";

				if (
					error?.response?.status === 401 &&
					!skipServerErrorBannerStatusCodes.includes("401")
				) {
					if (
						(
							error.response.data?.child_exceptions?.map(
								ex => ex.exception_type
							) || new Array<ChildException>()
						)?.includes(ExceptionType.ReAuthorizeException)
					) {
						if (isSuperOpsReauthReloadInProgress()) {
							throw new ServerError(error);
						}
						const hint = cache.get(
							ZomentumStorageKeys.SUPEROPS_HINT,
							true
						);
						const alreadyRetried = cache.get(
							ZomentumStorageKeys.SUPEROPS_SSO_RETRIED,
							true
						);
						if (isEmbedded() && hint && !alreadyRetried) {
							markSuperOpsReauthReloadInProgress();
							cache.save(
								ZomentumStorageKeys.SUPEROPS_SSO_RETRIED,
								true
							);
							const region = cache.get(
								ZomentumStorageKeys.SUPEROPS_REGION,
								true
							);
							const retryUrl =
								prependBasePath(ZomentumRoutes.Authorize) +
								`?superops_hint=${encodeURIComponent(
									hint
								)}&embed=true` +
								(region
									? `&region=${encodeURIComponent(region)}`
									: "");
							window.location.replace(retryUrl);
							throw new ServerError(error);
						}
						ZMessage.success(
							error?.response?.data?.message,
							2,
							() => logout(auth0)
						);
						return;
					} else if (
						error?.config &&
						!error.config.headers["retry"]
					) {
						const existingToken = cache.get(
							ZomentumStorageKeys.Token,
							true
						)?.token;
						if (!existingToken) {
							throw new ServerError(error);
						}
						let token = "";
						try {
							token = await auth0.getAccessTokenSilently();
						} catch (e) {
							console.warn(e);
							return logout(auth0);
						}
						const tokenInCache = existingToken;
						if (tokenInCache && token && token.length > 0) {
							error.config.headers[
								"Authorization"
							] = `Bearer ${token}`;
							error.config.headers["retry"] = true;
							cache.save(
								ZomentumStorageKeys.Token,
								new AuthToken(token)
							);
							return axios(error.config);
						} else {
							logout(auth0);
						}
					} else {
						logout(auth0);
					}
				}

				if (
					error?.response?.status === 403 &&
					!skipServerErrorBannerStatusCodes.includes("403")
				) {
					ZMessage.error("Unauthorized to perform the action");
					return;
				}

				const mergeEntityId =
					error?.response?.data?.error_details?.merged_entity_id;
				if (mergeEntityId) {
					let url = "";
					const pathname = getAppPathname();
					if (
						matchPath(pathname, ZomentumRoutes.ClientsDetail)
							?.isExact
					) {
						url = ZomentumRoutes.ClientsDetail.replace(
							":id",
							mergeEntityId
						);
					} else if (
						matchPath(pathname, ZomentumRoutes.OpportunityDetail)
							?.isExact
					) {
						url = ZomentumRoutes.OpportunityDetail.replace(
							":id",
							mergeEntityId
						);
					} else if (
						matchPath(pathname, ZomentumRoutes.DocumentsPreview)
							?.isExact
					) {
						if (
							window.location.search.includes(
								QueryParamConstants.CLIENT_USER_ID
							)
						) {
							url =
								window.location.pathname +
								"?" +
								QueryParamConstants.CLIENT_USER_ID +
								"=" +
								mergeEntityId;
						}
					}
					if (url) {
						props.history.push(url);
						window.location.reload();
						throw new ServerError(error);
					}
				}

				if (
					!skipServerErrorBannerStatusCodes
						.split(",")
						.includes(error?.response?.status?.toString())
				) {
					ZMessage.serverError({
						errorData: error,
						reportData: {
							userData,
							currentActiveOpportunity,
							currentActiveReport,
							currentActivePipeline,
							currentActiveClient
						},
						currentLocationPath: window.location.pathname
					});
				}

				if (
					error?.response?.status === 500 &&
					!error.config?.headers["Skip-500-Server-Error-Redirection"]
				) {
					props.history.push(ZomentumRoutes.Error);
				}
				throw new ServerError(error);
			}
		);
	}, []);

	const { configurationSettings } = useSelector((state: GlobalState) => ({
		configurationSettings: state.user.configurationSettings
	}));

	if (!tokenReady) return <ZContentLoadingIcon />;

	return (
		<>
			{!isPdfView && !isOnAuthorize && <Banner />}
			{!isOnAuthorize && <ZPaymentDueBanner />}
			{/* <AppUpdateBanner updateServiceWorker={props.updateServiceWorker} /> */}
			{!isPdfView && !isOnAuthorize && (
				<>
					<CancelledAccountModal />
					<OnBoardingModal />
				</>
			)}
			<DashboardLayout updateServiceWorker={props.updateServiceWorker}>
				<Suspense fallback={<ZContentLoadingIcon />}>
					{!isPasswordlessError && <PartnerDashboardRoutes />}
				</Suspense>

				{!isPdfView && <SalesActivityDrawers />}
				{!isPdfView && (
					<>
						<EditProductToQuoteBlockDrawer />
					</>
				)}
			</DashboardLayout>
			{!isOnAuthorize && <PreSetupAccountLoadThirdPartyScripts />}
			{setupDone(configurationSettings) && !isOnAuthorize && (
				<PostSetupAccountLoadThirdPartyScripts />
			)}
		</>
	);
};

export default withRouter((props: Props) => {
	return <AppDashboard {...props} />;
});
